// src/components/websearch/MessageSelector.js
import React, { useState, useEffect } from 'react';
import { 
  X, MessageCircle, Mail, Send, Loader2, Check, 
  AlertCircle, Search
} from 'lucide-react';
import { websearchService } from '../../services/websearchService';

export const MessageSelector = ({ bookId, lead, type, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMessages();
  }, [bookId, type]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await websearchService.getLeadMessages(bookId);
      setMessages(data.filter(m => m.type === type));
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!selectedMessage) return;

    const personalizedContent = personalizeMessage(selectedMessage.content, lead);
    
    if (type === 'whatsapp') {
      const phoneNumber = lead.phone?.replace(/\D/g, '');
      const encodedMessage = encodeURIComponent(personalizedContent);
      window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
    } else {
      const subject = personalizeMessage(selectedMessage.subject || 'Message from Lead Scout', lead);
      const body = encodeURIComponent(personalizedContent);
      window.open(`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
    }

    onClose();
  };

  const personalizeMessage = (text, lead) => {
    return text
      .replace(/\{name\}/g, lead.name || '')
      .replace(/\{company\}/g, lead.company || '')
      .replace(/\{email\}/g, lead.email || '')
      .replace(/\{phone\}/g, lead.phone || '');
  };

  const filteredMessages = messages.filter(msg =>
    msg.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (msg.subject && msg.subject.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-900 w-full max-w-2xl max-h-[80vh] rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-800 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              {type === 'whatsapp' ? (
                <MessageCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Mail className="h-5 w-5 text-blue-500" />
              )}
              Select {type === 'whatsapp' ? 'WhatsApp' : 'Email'} Message
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Sending to: <span className="text-white">{lead.name || lead.email || lead.phone}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="h-6 w-6 text-slate-400" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No {type} messages found
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map(msg => (
                <button
                  key={msg.id}
                  onClick={() => setSelectedMessage(msg)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedMessage?.id === msg.id
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-sm font-medium">
                      {msg.category}
                    </span>
                    {selectedMessage?.id === msg.id && (
                      <Check className="h-5 w-5 text-indigo-500" />
                    )}
                  </div>
                  {msg.subject && (
                    <p className="font-medium text-white mb-1">{msg.subject}</p>
                  )}
                  <p className="text-slate-300 text-sm line-clamp-2">{msg.content}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        {selectedMessage && (
          <div className="border-t border-slate-800 p-4 bg-slate-800/50">
            <p className="text-sm text-slate-400 mb-2">Preview:</p>
            <div className="bg-slate-900 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm text-slate-300 whitespace-pre-wrap">
                {personalizeMessage(selectedMessage.content, lead)}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-slate-800 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedMessage}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            Send {type === 'whatsapp' ? 'WhatsApp' : 'Email'}
          </button>
        </div>
      </div>
    </div>
  );
};